import type { UndefinedOnPartialDeep } from 'type-fest';

import type { DtoArticle, DtoDeliveryArrangement, DtoProvidedService } from '@repo/api-types/order-retrieval';
import type { AssetArticle, AssetDataType } from '@repo/types/asset';
import {
    type DraftAsset,
    type BookedServiceProposal as DraftBookedServiceProposal,
    isBookedServiceProposal,
} from '@repo/types/draft';
import { type OrderAsset, isOrder } from '@repo/types/order';
import type { QuoteAsset, BookedServiceProposal as QuoteBookedServiceProposal } from '@repo/types/quotation';
import type { Service } from '@repo/types/service';
import type { TimeWindow } from '@repo/types/time-windows';
import { normalizeToUtc, resolveTimezone } from '@repo/utils';
import { filterNonNullProperties } from '@repo/utils/type.utils';

import { buildCompositeServiceId } from '../services/services.api.utils';
import { selectAssetArticles } from './cart.selectors';
import { getAssetData } from './customer-assets.selectors';

const selectServicesFn = getAssetData(
    (data) => data.assetDetails._embedded?.providedServices ?? [],
    (data) => data.assetDetails.cartData?.bookedServiceProposal,
    (data) => data.assetDetails.cartData?.bookedServiceProposal
);

export function selectRawProvidedServices(data: OrderAsset): DtoProvidedService[];
export function selectRawProvidedServices(data: DraftAsset): DraftBookedServiceProposal | undefined;
export function selectRawProvidedServices(data: QuoteAsset): QuoteBookedServiceProposal | undefined;
export function selectRawProvidedServices(data?: AssetDataType) {
    return selectServicesFn(data);
}

interface SelectServicesOptions {
    fallbackTimezones?: ReadonlyArray<string | undefined>;
}

export const selectServices = (data?: AssetDataType, options?: SelectServicesOptions) => {
    const services = selectServicesFn(data);
    const articles = isOrder(data) ? data.assetDetails._embedded?.Articles : [];
    const items = selectAssetArticles(data);
    const deliveryArrangements = isOrder(data) ? data.assetDetails._embedded?.deliveryArrangements : [];
    const salesPriceData = selectSalesPriceData(data);

    const list =
        isOrder(data) && Array.isArray(services)
            ? getOrderServices(services, articles, deliveryArrangements, items, options)
            : isBookedServiceProposal(services)
              ? getAssetServices({ services, items, salesPriceData, ...(options && { options }) })
              : [];

    return list;
};

export type SelectedServices = ReturnType<typeof selectServices>;

const selectSalesPriceDataFn = getAssetData(
    (_data) => null,
    (data) => data.assetDetails.cartData?.salesPriceData,
    (data) => data.assetDetails.cartData?.salesPriceData
);

export const selectSalesPriceData = (data?: AssetDataType) => {
    return selectSalesPriceDataFn(data);
};

const matchItemToRefItem = (
    refItemId?: string,
    items?: AssetArticle[],
    refItemLineId?: string | number
): AssetArticle | undefined => {
    const referenceId = Number(refItemLineId);

    if ((!refItemId && !Number.isFinite(referenceId)) || !items || items.length === 0) {
        return;
    }

    for (const item of items) {
        if ((refItemId && item.itemId === refItemId) || item.referenceId === referenceId) {
            return item;
        }

        const matchedChildItem = matchItemToRefItem(refItemId, item.childItems, refItemLineId);
        if (matchedChildItem) {
            return matchedChildItem;
        }
    }

    return;
};

const mapOrderServiceRefItems = (el: DtoArticle, items?: AssetArticle[]) => {
    const matchedItem = matchItemToRefItem(el.itemId, items);
    const { name: matchedItemName, imageUrl, description, isReferenceItem } = matchedItem ?? {};
    const { itemId, name, quantity, itemType, quantityType, seqNo } = el;

    return {
        itemId: itemId ?? '',
        name: matchedItemName ?? name ?? '',
        description: description ?? '',
        quantity: normalizeServiceQuantity(quantity) ?? 0,
        image: imageUrl ?? '',
        retailItemName: matchedItemName ?? '',
        retailItemDescription: description ?? '',
        isReferenceItem: isReferenceItem ?? el.isReferenceItem ?? false,
        itemType,
        quantityType,
        seqNo,
    };
};

const normalizeServiceQuantity = (quantity: unknown): number | undefined => {
    if (typeof quantity === 'number') {
        return Number.isFinite(quantity) ? quantity : undefined;
    }

    if (typeof quantity !== 'string') {
        return;
    }

    const trimmedQuantity = quantity.trim();
    if (trimmedQuantity.length === 0) {
        return;
    }

    const normalizedQuantity = Number(trimmedQuantity);
    return Number.isFinite(normalizedQuantity) ? normalizedQuantity : undefined;
};

interface AssetServiceReferenceItem {
    imageUrl?: string | undefined;
    itemDescription?: string | undefined;
    itemId?: string | undefined;
    itemName?: string | undefined;
    lineId?: unknown;
    name?: string | undefined;
    quantity?: unknown;
}

const isReferenceItemLineId = (lineId: unknown): lineId is string | number =>
    typeof lineId === 'string' || typeof lineId === 'number';

const mapAssetServiceReferenceItem = (
    refItem: AssetServiceReferenceItem,
    items?: AssetArticle[]
): NonNullable<Service['referenceItems']>[number] => {
    const lineId = isReferenceItemLineId(refItem.lineId) ? refItem.lineId : undefined;
    const matchedItem = matchItemToRefItem(refItem.itemId, items, lineId);
    const {
        itemId: matchedItemId,
        name: matchedItemName,
        description: matchedItemDescription,
        imageUrl,
        isReferenceItem,
    } = matchedItem ?? {};
    const itemId = [refItem.itemId, matchedItemId].find((value) => value);
    const name = [matchedItemName, refItem.name, refItem.itemName].find((value) => value);
    const description = [matchedItemDescription, refItem.itemDescription].find((value) => value);
    const image = [refItem.imageUrl, imageUrl].find((value) => value);
    const quantity = normalizeServiceQuantity(refItem.quantity);

    return {
        ...(itemId ? { itemId } : {}),
        ...(name ? { name } : {}),
        ...(description ? { description } : {}),
        ...(quantity !== undefined ? { quantity } : {}),
        ...(image ? { image } : {}),
        retailItemName: matchedItemName ?? '',
        retailItemDescription: matchedItemDescription ?? '',
        isReferenceItem: isReferenceItem ?? false,
    };
};

/**
 * Builds a stable, unique-per-instance identifier for a service section.
 *
 * Sibling services can share serviceId (e.g. two home-delivery subDeliveries with the same
 * serviceItemId), so it is unusable as a UI key. We prefer the first available stable id
 * (delivery/line identifier) and fall back to a fingerprint of the reference items, which is
 * what actually differs between sibling sections.
 */
const buildServiceInstanceId = (
    prefix: string,
    candidateIds: ReadonlyArray<string | number | undefined>,
    referenceItems: ReadonlyArray<{ itemId?: string | undefined; quantity?: number | undefined }>
): string => {
    const primaryId = candidateIds.find((value) => value !== undefined && value !== '');

    if (primaryId !== undefined) {
        return `${prefix}-${primaryId}`;
    }

    const fingerprint =
        referenceItems.length > 0
            ? referenceItems.map((item) => `${item.itemId ?? ''}:${item.quantity ?? ''}`).join('|')
            : 'none';

    return `${prefix}-${fingerprint}`;
};

const resolveTimeWindowTimezone = (timeZone: string | undefined, options?: SelectServicesOptions): string =>
    resolveTimezone(timeZone, options?.fallbackTimezones);

const normalizeTimeWindowDate = (dateTime: string, timeZone: string): string => normalizeToUtc(dateTime, timeZone);

interface BuildNormalizedTimeWindowParams {
    fulfillmentUnit?: TimeWindow['fulfillmentUnit'] | undefined;
    fromDateTime?: string | undefined;
    options?: SelectServicesOptions | undefined;
    paymentCutOffDateTime?: string | undefined;
    timeWindowId?: string | undefined;
    timeZone?: string | undefined;
    toDateTime?: string | undefined;
    tspName?: string | undefined;
}

const buildNormalizedTimeWindow = ({
    fulfillmentUnit,
    fromDateTime,
    options,
    paymentCutOffDateTime,
    timeWindowId,
    timeZone,
    toDateTime,
    tspName,
}: BuildNormalizedTimeWindowParams): TimeWindow => {
    const resolvedTimeZone = resolveTimeWindowTimezone(timeZone, options);

    return {
        timeWindowId: timeWindowId ?? '',
        fromDateTimeUtc: fromDateTime ? normalizeTimeWindowDate(fromDateTime, resolvedTimeZone) : '',
        toDateTimeUtc: toDateTime ? normalizeTimeWindowDate(toDateTime, resolvedTimeZone) : '',
        tspName,
        timeZone: resolvedTimeZone,
        paymentCutOffDateTimeUtc: paymentCutOffDateTime
            ? normalizeTimeWindowDate(paymentCutOffDateTime, resolvedTimeZone)
            : undefined,
        fulfillmentUnit,
    };
};

const getServiceTimeWindowSortValue = (service: UndefinedOnPartialDeep<Service>): number => {
    const sortValue = Date.parse(service.timeWindow?.fromDateTimeUtc ?? '');

    return Number.isNaN(sortValue) ? Number.POSITIVE_INFINITY : sortValue;
};

const sortServicesByTimeWindowStart = (
    services: UndefinedOnPartialDeep<Service[]>
): UndefinedOnPartialDeep<Service[]> =>
    services
        .map((service, index) => ({
            index,
            service,
            sortValue: getServiceTimeWindowSortValue(service),
        }))
        .sort((firstService, secondService) => {
            if (firstService.sortValue === secondService.sortValue) {
                return firstService.index - secondService.index;
            }

            return firstService.sortValue - secondService.sortValue;
        })
        .map(({ service }) => service);

interface GetAssetServicesParams {
    services?: Partial<DraftBookedServiceProposal | QuoteBookedServiceProposal>;
    items?: AssetArticle[];
    salesPriceData?: ReturnType<typeof selectSalesPriceData>;
    options?: SelectServicesOptions;
}

const getAssetServices = ({
    services,
    items,
    salesPriceData,
    options,
}: GetAssetServicesParams): UndefinedOnPartialDeep<Service[]> => {
    const deliveryServices: UndefinedOnPartialDeep<Service[]> = services?.bookedServices
        ? services.bookedServices
              .filter((service) => !service.isProvidedService)
              .flatMap((delivery) =>
                  delivery.subDeliveries?.map((subDelivery) => ({
                      delivery,
                      subDelivery,
                  }))
              )
              .filter((delivery) => delivery !== undefined)
              .map(({ delivery, subDelivery }) => {
                  const {
                      id,
                      serviceItemId,
                      deliveryId,
                      price,
                      discountPrice,
                      timeWindow,
                      referenceItems,
                      statusTranslated,
                      pickupPoint,
                      capabilityOptions,
                  } = subDelivery;
                  const { isProvidedService, fulfillmentMethod, fulfillmentType, serviceName } = delivery;

                  const deliveryServices = salesPriceData?.deliveryServices ?? [];

                  const savings =
                      deliveryServices
                          .find(({ lineId }) => lineId === deliveryId)
                          ?.deliveryPrice?.flatMap(({ savings }) => savings ?? []) ?? [];

                  const mappedReferenceItems = referenceItems.map((refItem) =>
                      mapAssetServiceReferenceItem(refItem, items)
                  );

                  return {
                      serviceId: serviceItemId,
                      serviceInstanceId: buildServiceInstanceId(
                          'delivery',
                          [subDelivery.deliveryId, id, subDelivery.lineId],
                          mappedReferenceItems
                      ),
                      //   isProvidedService property used as delivery differentiator as it's only present on other services and is missing on deliveries
                      isDelivery: !isProvidedService,
                      serviceType: fulfillmentMethod,
                      serviceName,
                      price: discountPrice ?? price,
                      ...(timeWindow && {
                          timeWindow: buildNormalizedTimeWindow({
                              timeWindowId: timeWindow.timeWindowId,
                              fromDateTime: timeWindow.fromDateTime,
                              toDateTime: timeWindow.toDateTime,
                              timeZone: timeWindow.timeZone,
                              tspName: timeWindow.tspName,
                              paymentCutOffDateTime: timeWindow.paymentCutOffDateTime,
                              fulfillmentUnit: timeWindow.fulfillingUnit,
                              options,
                          }),
                      }),
                      serviceProviderName: timeWindow?.tspName,
                      referenceItems: mappedReferenceItems,
                      ...(statusTranslated && { statusText: statusTranslated }),
                      ...(pickupPoint && {
                          pickupPoint: {
                              address: {
                                  addressLine1: pickupPoint.addressLine1,
                                  addressLine4: pickupPoint.addressLine4,
                                  city: pickupPoint.city,
                                  country: pickupPoint.country,
                                  stateCode: pickupPoint.state,
                                  zipCode: pickupPoint.zipCode,
                              },
                              distance: Number(pickupPoint.distance),
                              identifier: pickupPoint.identifier,
                              lsc: {
                                  buType: pickupPoint.lsc?.split('.')[0],
                                  buCode: pickupPoint.lsc?.split('.')[1],
                              },
                              name: pickupPoint.name,
                              openingHours: Object.entries(pickupPoint)
                                  .filter(([key]) => key.startsWith('openingHours'))
                                  .map(([key, value]) => ({
                                      dayOfWeek: key,
                                      // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
                                      span: value as string,
                                  })),
                          },
                      }),
                      deliveryDestinationType: fulfillmentType,
                      capabilities: capabilityOptions,
                      savings,
                  };
              })
              .map((el) => filterNonNullProperties(el))
        : [];

    const servicesList: UndefinedOnPartialDeep<Service[]> = services?.bookedServices
        ? services.bookedServices
              .filter((service) => service.isProvidedService)
              .map((service) => {
                  const {
                      serviceItemId,
                      isProvidedService,
                      serviceType,
                      serviceName,
                      serviceProviderPreferredPayment,
                      price,
                      timeWindow,
                      referenceItems,
                      statusTranslated,
                      serviceProviderName,
                      itemRequiredForService,
                  } = service;

                  // Some provided services (e.g. furniture disposal) are item-independent and still
                  // carry every selected cart item as referenceItems. They must not list any articles.
                  const mappedReferenceItems =
                      itemRequiredForService === false
                          ? []
                          : referenceItems.map((refItem) => mapAssetServiceReferenceItem(refItem, items));

                  return {
                      serviceId: serviceItemId,
                      serviceInstanceId: buildServiceInstanceId(
                          'provided',
                          [service.id, service.lineId, serviceItemId],
                          mappedReferenceItems
                      ),
                      //   isProvidedService property used as delivery differentiator as it's only present on other services and is missing on deliveries
                      isDelivery: !isProvidedService,
                      serviceType,
                      serviceName,
                      servicePaymentTo: serviceProviderPreferredPayment,
                      price,
                      ...(timeWindow && {
                          timeWindow: buildNormalizedTimeWindow({
                              // TimeWindow4 type uses 'id' instead of 'timeWindowId'
                              timeWindowId: timeWindow.id,
                              fromDateTime: timeWindow.fromDateTime,
                              toDateTime: timeWindow.toDateTime,
                              timeZone: timeWindow.timeZone,
                              tspName: serviceProviderName ?? '',
                              paymentCutOffDateTime: timeWindow.paymentCutOffDateTime,
                              options,
                          }),
                      }),
                      serviceProviderName: serviceProviderName,
                      referenceItems: mappedReferenceItems,
                      ...(statusTranslated && { statusText: statusTranslated }),
                  };
              })
              .map((el) => filterNonNullProperties(el))
        : [];

    return sortServicesByTimeWindowStart([...deliveryServices, ...servicesList]);
};

// oxlint-disable-next-line max-params
function getOrderServices(
    services?: DtoProvidedService[],
    articles?: DtoArticle[],
    deliveryArrangements?: DtoDeliveryArrangement[],
    items?: AssetArticle[],
    options?: SelectServicesOptions
): UndefinedOnPartialDeep<Service[]> {
    const serviceList: UndefinedOnPartialDeep<Service[]> = services
        ? services
              .map((service) => {
                  const {
                      serviceItemNo,
                      serviceItemType,
                      providedServicePriceDetails,
                      serviceProductId,
                      serviceName,
                      servicePaymentTo,
                      serviceProviderName,
                      price,
                      timeWindows,
                      referencedItems,
                      statusText,
                      lineId,
                  } = service;

                  return {
                      serviceId: buildCompositeServiceId(serviceItemType, serviceItemNo),
                      serviceInstanceId: lineId === undefined ? undefined : `provided-${lineId}`,
                      //   isDelivery is assigned based on providedServicePriceDetails property as it should be present on provided services and is missing on deliveries.
                      //   If providedServicePriceDetails is present, then service is not a delivery and 'false' should be assigned.
                      isDelivery: !providedServicePriceDetails,
                      serviceType: serviceProductId,
                      serviceName,
                      servicePaymentTo,
                      serviceProviderName,
                      price,
                      ...(timeWindows?.[0] && {
                          timeWindow: buildNormalizedTimeWindow({
                              timeWindowId: timeWindows[0].timeWindowId,
                              fromDateTime: timeWindows[0].fromLocalDateTime,
                              toDateTime: timeWindows[0].toLocalDateTime,
                              timeZone: timeWindows[0].timeZone,
                              tspName: serviceProviderName ?? '',
                              paymentCutOffDateTime: timeWindows[0].paymentCutOffDateTime,
                              fulfillmentUnit: timeWindows[0].transferringUnit,
                              options,
                          }),
                      }),
                      ...(referencedItems &&
                          items && {
                              referenceItems: referencedItems
                                  .map((refId) =>
                                      articles?.find(
                                          (article) =>
                                              Number(article.externalRefId) === refId || article.soSalesItemNo === refId
                                      )
                                  )
                                  .filter((el): el is DtoArticle => Boolean(el))
                                  .map((el) => mapOrderServiceRefItems(el, items)),
                          }),
                      statusText,
                  };
              })
              .map((el) => filterNonNullProperties(el))
        : [];

    const deliveryServices: UndefinedOnPartialDeep<Service[]> = deliveryArrangements
        ? deliveryArrangements
              .flatMap((el) =>
                  el._embedded?.deliveryServices?.map((service) => ({ ...service, serviceType: el.serviceType }))
              )
              .filter((el) => el !== undefined)
              .map((service) => {
                  const {
                      deliveryNo,
                      serviceType,
                      serviceName,
                      tspName,
                      price,
                      deliveryPriceDetails,
                      timeWindows,
                      referencedItems,
                      status,
                      statusCode,
                      deliveryDestinationType,
                      pickupPoint,
                      capabilities,
                  } = service;

                  return {
                      serviceId: service.serviceId,
                      serviceInstanceId: deliveryNo === undefined ? undefined : `delivery-${deliveryNo}`,
                      deliveryNo,
                      //   isDelivery is assigned based on deliveryPriceDetails (missing on FS deliveries) or deliveryDestinationType (fallback to catch FS deliveries) properties which are present on deliveries and are missing on other services.
                      //   If deliveryPriceDetails or deliveryDestinationType are present, then service is a delivery and 'true' should be assigned.
                      isDelivery: Boolean(deliveryPriceDetails) || Boolean(deliveryDestinationType),
                      serviceType,
                      serviceName,
                      serviceProviderName: tspName,
                      price: deliveryPriceDetails?.inclSavings ?? price,
                      ...(timeWindows?.[0] && {
                          timeWindow: buildNormalizedTimeWindow({
                              timeWindowId: timeWindows[0].timeWindowId,
                              fromDateTime: timeWindows[0].fromLocalDateTime,
                              toDateTime: timeWindows[0].toLocalDateTime,
                              timeZone: timeWindows[0].timeZone,
                              tspName: tspName ?? '',
                              paymentCutOffDateTime: timeWindows[0].paymentCutOffDateTime,
                              fulfillmentUnit: timeWindows[0].transferringUnit,
                              options,
                          }),
                      }),
                      ...(referencedItems &&
                          items && {
                              referenceItems: referencedItems
                                  .map((refId) => articles?.find((article) => Number(article.externalRefId) === refId))
                                  .filter((el): el is DtoArticle => Boolean(el))
                                  .map((el) => mapOrderServiceRefItems(el, items)),
                          }),
                      savings: deliveryPriceDetails?.savings ?? [],
                      status,
                      statusCode,
                      deliveryDestinationType,
                      pickupPoint,
                      capabilities,
                  };
              })
              .map((el) => filterNonNullProperties(el))
        : [];

    return sortServicesByTimeWindowStart([...serviceList, ...deliveryServices]);
}
